import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAdminAuth } from "../context/AdminAuth";
import { adminApi, apiError } from "../lib/api";
import { useRefetchOnFocus } from "../hooks/useRefetchOnFocus";
import { Sparkles, Users, Coins, MailWarning, Activity, LogOut, Plus, Search, QrCode, ArrowRight, Camera, Gift, Scissors, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { QrScanner } from "../components/QrScanner";

function TierBadge({ tier }) {
  const map = {
    Bronze: "bg-amber-100 text-amber-900 border-amber-300",
    Silver: "bg-slate-100 text-slate-800 border-slate-300",
    Gold: "bg-amber-500/15 text-amber-900 border-amber-400/50",
    Platinum: "bg-stone-900 text-amber-200 border-amber-500/30",
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${map[tier]}`}>{tier}</span>;
}

function LoginScreen() {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Willkommen zurück");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#4A1D24] flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-2 text-[#4A1D24]">
          <Sparkles className="h-5 w-5 text-[#C59B6C]" />
          <span className="text-xs tracking-[0.3em] font-semibold">VELOURS &amp; SILK</span>
        </div>
        <h1 className="font-serif text-3xl mt-4 text-stone-900">Salon-Admin</h1>
        <p className="text-sm text-stone-500 mt-1 mb-6">Bitte melden Sie sich an.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">E-Mail</Label>
            <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-stone-500">Passwort</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
          </div>
          <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-[#4A1D24] hover:bg-[#3a161c] text-white">
            {loading ? "Anmelden…" : "Anmelden"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5" data-testid={testid}>
      <div className={`inline-flex p-2 rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
      <p className="text-2xl font-semibold text-stone-900 mt-3">{value}</p>
      <p className="text-xs uppercase tracking-widest text-stone-400 mt-0.5">{label}</p>
    </div>
  );
}

function Dashboard() {
  const { admin, logout } = useAdminAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [scanToken, setScanToken] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", marketing_consent: false });

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([adminApi.get("/admin/stats"), adminApi.get("/admin/customers", { params: { q } })]);
      setStats(s.data);
      setCustomers(c.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  const createCustomer = async () => {
    try {
      const r = await adminApi.post("/admin/customers", form);
      toast.success("Kundin angelegt");
      setAddOpen(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", marketing_consent: false });
      nav(`/admin/customers/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const scanByToken = async (token) => {
    const t = (token || "").trim();
    if (!t) return;
    try {
      const r = await adminApi.get(`/admin/scan/${t}`);
      toast.success(`Kundin gefunden: ${r.data.first_name}`);
      nav(`/admin/customers/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const scan = () => scanByToken(scanToken);
  const onCameraScan = (decoded) => { setScanOpen(false); scanByToken(decoded); };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#4A1D24] text-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#C59B6C]" />
            <div>
              <p className="text-xs tracking-[0.3em] font-semibold">VELOURS &amp; SILK</p>
              <p className="text-[11px] text-stone-300/70">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button data-testid="nav-catalog-button" variant="ghost" onClick={() => nav("/admin/catalog")} className="text-stone-200 hover:text-white hover:bg-white/10">
              <Gift className="h-4 w-4 mr-1.5" /> Katalog
            </Button>
            <Button data-testid="nav-services-button" variant="ghost" onClick={() => nav("/admin/services")} className="text-stone-200 hover:text-white hover:bg-white/10">
              <Scissors className="h-4 w-4 mr-1.5" /> Dienstleistungen
            </Button>
            <Button data-testid="nav-stats-button" variant="ghost" onClick={() => nav("/admin/statistics")} className="text-stone-200 hover:text-white hover:bg-white/10">
              <BarChart3 className="h-4 w-4 mr-1.5" /> Statistik
            </Button>
            <Button data-testid="admin-logout-button" variant="ghost" onClick={logout} className="text-stone-200 hover:text-white hover:bg-white/10">
              <LogOut className="h-4 w-4 mr-1.5" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-customers" icon={Users} label="Kundinnen" value={stats?.total_customers ?? "…"} accent="bg-stone-100 text-stone-700" />
          <StatCard testid="stat-points" icon={Coins} label="Punkte gesamt" value={stats?.total_points ?? "…"} accent="bg-amber-100 text-amber-800" />
          <StatCard testid="stat-today" icon={Activity} label="Änderungen heute" value={stats?.transactions_today ?? "…"} accent="bg-emerald-50 text-emerald-700" />
          <StatCard testid="stat-failed" icon={MailWarning} label="Fehlgesch. E-Mails" value={stats?.failed_emails ?? "…"} accent="bg-rose-50 text-rose-700" />
        </div>

        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-8 rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm">
            <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-b border-stone-100">
              <h2 className="font-serif text-2xl text-stone-900">Kundinnen</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input data-testid="customer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="pl-8 h-9 w-40 sm:w-52" />
                </div>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-customer-button" className="bg-[#4A1D24] hover:bg-[#3a161c] text-white h-9"><Plus className="h-4 w-4 mr-1" /> Kundin</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl">Neue Kundin</DialogTitle>
                      <DialogDescription>Legen Sie ein neues Kundenkonto an.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Vorname</Label><Input data-testid="new-firstname-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                        <div><Label className="text-xs">Nachname</Label><Input data-testid="new-lastname-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                      </div>
                      <div><Label className="text-xs">E-Mail</Label><Input data-testid="new-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                      <div><Label className="text-xs">Telefon (optional)</Label><Input data-testid="new-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                      <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
                        <span className="text-sm text-stone-600">Marketing-Einwilligung</span>
                        <Switch data-testid="new-marketing-switch" checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: v })} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button data-testid="save-customer-button" onClick={createCustomer} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">Anlegen</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div className="divide-y divide-stone-100" data-testid="admin-customer-table">
              {customers.length === 0 && <p className="p-6 text-sm text-stone-400">Keine Kundinnen gefunden.</p>}
              {customers.map((c) => (
                <button
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  onClick={() => nav(`/admin/customers/${c.id}`)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 text-left transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-stone-900 truncate">{c.first_name} {c.last_name}</p>
                    <p className="text-xs text-stone-400 truncate">{c.email}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <TierBadge tier={c.tier} />
                    <span className="font-mono text-sm font-semibold text-[#4A1D24]">{c.points} P</span>
                    <ArrowRight className="h-4 w-4 text-stone-300" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5">
              <div className="flex items-center gap-2 text-[#4A1D24]"><QrCode className="h-5 w-5" /><h3 className="font-semibold">QR-Code Kundin finden</h3></div>
              <p className="text-xs text-stone-500 mt-1">Karte scannen oder gescannten QR-Token eingeben.</p>
              <Dialog open={scanOpen} onOpenChange={setScanOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="open-scanner-button" className="w-full mt-3 bg-[#4A1D24] hover:bg-[#3a161c] text-white"><Camera className="h-4 w-4 mr-1.5" /> Kamera-Scanner öffnen</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">QR-Code scannen</DialogTitle>
                    <DialogDescription>Halten Sie die Kundenkarte vor die Kamera.</DialogDescription>
                  </DialogHeader>
                  {scanOpen && <QrScanner onScan={onCameraScan} />}
                  <p className="text-xs text-stone-400 text-center">Bei Bedarf Kamerazugriff im Browser erlauben.</p>
                </DialogContent>
              </Dialog>
              <div className="flex gap-2 mt-3">
                <Input data-testid="scan-token-input" value={scanToken} onChange={(e) => setScanToken(e.target.value)} placeholder="QR-Token manuell" />
                <Button data-testid="scan-lookup-button" onClick={scan} variant="outline" className="border-[#C59B6C] text-[#4A1D24]">Finden</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const { admin } = useAdminAuth();
  if (admin === null) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-stone-400">Lädt…</div>;
  if (!admin) return <LoginScreen />;
  return <Dashboard />;
}
